import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import RtcRoom from "@/components/app/RtcRoom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Video, Plus, Link2, LogOut, X, Users, ScreenShare, Smartphone, Copy, Check } from "lucide-react";

export default function WarRoom() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [title, setTitle] = useState("Team War Room");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null); // room object currently in
  const [copiedId, setCopiedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/warroom/rooms");
      setRooms(data?.items || []);
      return data?.items || [];
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load rooms");
      return [];
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the lobby's live participant counts fresh while browsing (not while in a call).
  useEffect(() => {
    if (joined) return;
    const t = setInterval(() => load(), 6000);
    return () => clearInterval(t);
  }, [joined, load]);

  // One-tap hop-on: /admin/warroom?join=<slug> drops you straight into that room.
  useEffect(() => {
    const slug = searchParams.get("join");
    if (!slug || joined) return;
    (async () => {
      const items = await load();
      const room = (items || []).find((r) => r.slug === slug);
      if (room) setJoined(room);
      else toast.error("That room has ended.");
      searchParams.delete("join");
      setSearchParams(searchParams, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const startRoom = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/warroom/rooms", { title: title.trim() || "Team War Room" });
      await load();
      setJoined(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start room");
    } finally { setBusy(false); }
  };

  const closeRoom = async (rid) => {
    if (!window.confirm("End this room for everyone?")) return;
    try {
      await api.post(`/warroom/rooms/${rid}/close`);
      if (joined?.id === rid) setJoined(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't close room");
    }
  };

  const copyLink = (rid) => {
    const url = `${window.location.origin}/room/${rid}`;
    navigator.clipboard?.writeText(url);
    setCopiedId(rid);
    setTimeout(() => setCopiedId(null), 1500);
    toast.success("Client invite link copied");
  };

  // In-call view
  if (joined) {
    return (
      <AppShell>
        <div className="h-[calc(100vh-70px)] flex flex-col" data-testid="warroom-incall">
          <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-900 text-white">
            <Video size={18} className="text-emerald-400" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-black leading-none truncate">{joined.title}</p>
              <p className="text-[11px] text-slate-400">Live room · screen-share works on computers & Android</p>
            </div>
            <Button size="sm" onClick={() => copyLink(joined.id)} className="bg-sky-500 hover:bg-sky-400 text-white font-bold shadow-sm" data-testid="incall-copy-link">
              {copiedId === joined.id ? <Check size={14} className="mr-1" /> : <Link2 size={14} className="mr-1" />} Copy client link
            </Button>
            <Button size="sm" onClick={() => setJoined(null)} className="bg-rose-600 hover:bg-rose-700" data-testid="incall-leave">
              <LogOut size={14} className="mr-1" /> Leave
            </Button>
          </div>
          <div className="flex-1 bg-black">
            <RtcRoom roomId={joined.slug} displayName={user?.name || "Staff"} onLeave={() => setJoined(null)} />
          </div>
        </div>
      </AppShell>
    );
  }

  // Lobby view
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="warroom-lobby">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-emerald-500 grid place-items-center shrink-0">
            <Video className="text-white" size={22} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black text-slate-900 leading-tight">Video Room</h1>
            <p className="text-slate-500 text-sm mt-1">Our own in-house video + screen share. Start a room, watch each other work, and drop clients a link to hop on — no WhatsApp or FaceTime needed.</p>
          </div>
        </div>

        {/* Start a room */}
        <Card className="p-5 rounded-2xl border-slate-100 mb-5">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-bold text-slate-500">Room name</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Team War Room" data-testid="warroom-title-input" />
            </div>
            <Button onClick={startRoom} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="warroom-start-btn">
              <Plus size={16} className="mr-1" /> Start room
            </Button>
          </div>
          <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><ScreenShare size={13} className="text-emerald-500" /> Screen-share on computers & Android</span>
            <span className="inline-flex items-center gap-1"><Smartphone size={13} className="text-amber-500" /> iPhone: video + mic + view (Apple blocks phone screen-share)</span>
            <span className="inline-flex items-center gap-1"><Users size={13} className="text-sky-500" /> Clients join by link, no login</span>
          </div>
        </Card>

        {/* Active rooms */}
        <h2 className="font-display text-lg font-black text-slate-800 mb-2">Active rooms</h2>
        {rooms.length === 0 && <p className="text-sm text-slate-400 py-3">No rooms running. Start one above.</p>}
        <div className="space-y-2">
          {rooms.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`warroom-row-${r.id}`}>
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${r.live ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-900 text-sm truncate">{r.title}</p>
                <p className="text-[11px] text-slate-400">
                  {r.participant_count > 0
                    ? <><span className="text-emerald-600 font-bold">{r.participant_count} on</span> · {(r.participants || []).join(", ")}</>
                    : <>Started by {r.created_by_name} · empty — be the first in</>}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copyLink(r.id)} className="text-sky-700 hover:bg-sky-50" data-testid={`warroom-copy-${r.id}`}>
                {copiedId === r.id ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />} Client link
              </Button>
              <Button size="sm" onClick={() => setJoined(r)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`warroom-join-${r.id}`}>
                <Video size={14} className="mr-1" /> Join
              </Button>
              <Button size="sm" variant="ghost" onClick={() => closeRoom(r.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`warroom-close-${r.id}`}>
                <X size={15} />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
